import type { TonyteThemeColors, ThemeName } from './types';

export const SEASONAL_THEME_SCHEMA_VERSION = 1;
export const SEASONAL_THEME_RENDERER_VERSION = 1;

export const SEASONAL_COLOR_KEYS = [
  'background', 'backgroundSecondary', 'surface', 'surfaceSoft', 'surfaceElevated',
  'surfaceGlass', 'border', 'borderStrong', 'cardBorder', 'accent', 'accentSecondary',
  'accentSecondarySoft', 'accentSecondaryBorder', 'chipSelectedText', 'chipBackgroundActive',
  'filterActiveIcon', 'navBackground', 'navIconActive', 'cardBackground', 'divider',
] as const satisfies readonly (keyof TonyteThemeColors)[];

export type SeasonalColorKey = typeof SEASONAL_COLOR_KEYS[number];
export type SeasonalColorOverrides = Partial<Pick<TonyteThemeColors, SeasonalColorKey>>;
export type SeasonalEffectType = 'none' | 'sparkles' | 'snow' | 'confetti';

export interface SeasonalThemeAssets {
  headerUrl?: string;
  backgroundUrl?: string;
  illustrationUrl?: string;
}

export interface SeasonalThemeVariant {
  colors: SeasonalColorOverrides;
  assets?: SeasonalThemeAssets;
}

export interface PublishedSeasonalTheme {
  id: string;
  name: string;
  description: string;
  schemaVersion: 1;
  minRendererVersion: number;
  version: number;
  published: true;
  enabled: boolean;
  priority: number;
  startAt: number;
  endAt: number;
  light: SeasonalThemeVariant;
  dark: SeasonalThemeVariant;
  effect: { type: SeasonalEffectType; intensity: number };
}

export type SeasonalThemeDocument = { id: string; data: Record<string, unknown> };

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const toMillis = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (isRecord(value) && typeof value.toMillis === 'function') return (value.toMillis as () => number)();
  return null;
};
const validColor = (value: unknown): value is string => typeof value === 'string' && (
  /^#[0-9a-f]{6}$/i.test(value) || /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/i.test(value)
);
const validAsset = (value: unknown) => value === undefined || value === '' || (typeof value === 'string' && /^https:\/\//i.test(value) && value.length <= 2048);

function parseVariant(value: unknown): SeasonalThemeVariant | null {
  if (!isRecord(value)) return null;
  const rawColors = isRecord(value.colors) ? value.colors : {};
  if (Object.keys(rawColors).some(key => !SEASONAL_COLOR_KEYS.includes(key as SeasonalColorKey))) return null;
  const colors: SeasonalColorOverrides = {};
  for (const key of SEASONAL_COLOR_KEYS) {
    const color = rawColors[key];
    if (color !== undefined) {
      if (!validColor(color)) return null;
      colors[key] = color;
    }
  }
  const rawAssets = value.assets;
  if (rawAssets !== undefined && !isRecord(rawAssets)) return null;
  const assets = rawAssets as Record<string, unknown> | undefined;
  if (assets && (!validAsset(assets.headerUrl) || !validAsset(assets.backgroundUrl) || !validAsset(assets.illustrationUrl))) return null;
  return {
    colors,
    ...(assets ? { assets: {
      ...(assets.headerUrl ? { headerUrl: String(assets.headerUrl) } : {}),
      ...(assets.backgroundUrl ? { backgroundUrl: String(assets.backgroundUrl) } : {}),
      ...(assets.illustrationUrl ? { illustrationUrl: String(assets.illustrationUrl) } : {}),
    } } : {}),
  };
}

export function parsePublishedSeasonalTheme(document: SeasonalThemeDocument): PublishedSeasonalTheme | null {
  const d = document.data;
  const startAt = toMillis(d.startAt);
  const endAt = toMillis(d.endAt);
  const light = parseVariant(d.light);
  const dark = parseVariant(d.dark);
  const effect = isRecord(d.effect) ? d.effect : {};
  const effectType = effect.type;
  const intensity = effect.intensity;
  if (
    d.schemaVersion !== SEASONAL_THEME_SCHEMA_VERSION || d.published !== true || d.enabled !== true ||
    typeof d.name !== 'string' || !d.name.trim() || d.name.length > 80 ||
    typeof d.description !== 'string' || d.description.length > 280 ||
    !Number.isInteger(d.version) || Number(d.version) < 1 ||
    !Number.isInteger(d.minRendererVersion) || Number(d.minRendererVersion) < 1 || Number(d.minRendererVersion) > SEASONAL_THEME_RENDERER_VERSION ||
    !Number.isInteger(d.priority) || Number(d.priority) < -1000 || Number(d.priority) > 1000 ||
    startAt === null || endAt === null || startAt >= endAt || !light || !dark ||
    !['none', 'sparkles', 'snow', 'confetti'].includes(String(effectType)) ||
    typeof intensity !== 'number' || intensity < 0 || intensity > 1
  ) return null;
  return {
    id: document.id,
    name: d.name.trim(),
    description: d.description,
    schemaVersion: 1,
    minRendererVersion: Number(d.minRendererVersion),
    version: Number(d.version),
    published: true,
    enabled: true,
    priority: Number(d.priority),
    startAt,
    endAt,
    light,
    dark,
    effect: { type: effectType as SeasonalEffectType, intensity },
  };
}

export function resolveSeasonalTheme(themes: PublishedSeasonalTheme[], now = Date.now()): PublishedSeasonalTheme | null {
  return themes
    .filter(theme => theme.enabled && theme.startAt <= now && now < theme.endAt)
    .sort((a, b) => b.priority - a.priority || b.startAt - a.startAt || a.id.localeCompare(b.id))[0] ?? null;
}

export function nextSeasonalBoundary(themes: PublishedSeasonalTheme[], now = Date.now()): number | null {
  const boundaries = themes.flatMap(theme => [theme.startAt, theme.endAt]).filter(value => value > now);
  return boundaries.length ? Math.min(...boundaries) : null;
}

export function seasonalAssetUrls(theme: PublishedSeasonalTheme): string[] {
  const urls = new Set<string>();
  for (const variant of [theme.light, theme.dark]) {
    Object.values(variant.assets ?? {}).forEach(url => { if (url) urls.add(url); });
  }
  return [...urls];
}

export function applySeasonalTheme(base: import('./types').TonyteTheme, seasonal: PublishedSeasonalTheme | null) {
  if (!seasonal) return base;
  const variant = base.name === ('daylight' as ThemeName) ? seasonal.light : seasonal.dark;
  return { ...base, colors: { ...base.colors, ...variant.colors } };
}

